import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  User as UserIcon,
  Bank,
  CheckCircle,
  ShieldCheck,
  Trash,
  FloppyDisk,
  Lightning,
  ArrowSquareOut,
  Warning,
} from "@phosphor-icons/react";
import {
  getProfile,
  updateProfile,
  updateBank,
  removeBank,
  connectStatus,
  connectOnboard,
  connectDisconnect,
} from "../lib/api";

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [bank, setBank] = useState({
    account_holder: "",
    sort_code: "",
    account_number: "",
    bank_name: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        getProfile(),
        connectStatus().catch(() => ({ configured: false, connected: false })),
      ]);
      setProfile(p);
      setConnect(c);
      setName(p.name || "");
      setPhone(p.phone || "");
      setAddress(p.address || "");
      if (p.bank) {
        setBank({
          account_holder: p.bank.account_holder || "",
          sort_code: p.bank.sort_code || "",
          account_number: "",
          bank_name: p.bank.bank_name || "",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startConnect = async () => {
    setConnectBusy(true);
    try {
      const r = await connectOnboard(window.location.origin + "/settings?connect=complete");
      window.location.href = r.onboarding_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start Stripe onboarding");
      setConnectBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Stripe account? Buyers will pay the platform until you reconnect.")) return;
    try {
      await connectDisconnect();
      toast.success("Stripe account disconnected");
      load();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const p = await updateProfile({ name, phone: phone || null, address: address || null });
      setProfile(p);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveBank = async () => {
    if (!bank.account_holder || !bank.sort_code || !bank.account_number) {
      toast.error("Fill in all bank fields");
      return;
    }
    setSavingBank(true);
    try {
      const r = await updateBank(bank);
      toast.success("Bank details saved — payouts enabled");
      setProfile((p) => ({ ...p, bank: r.bank, payouts_enabled: true }));
      setBank((b) => ({ ...b, account_number: "" }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save bank details");
    } finally {
      setSavingBank(false);
    }
  };

  const wipeBank = async () => {
    if (!window.confirm("Remove your bank details? You'll need to add them again to receive payouts.")) return;
    try {
      await removeBank();
      toast.success("Bank details removed");
      setProfile((p) => ({ ...p, bank: null, payouts_enabled: false }));
      setBank({ account_holder: "", sort_code: "", account_number: "", bank_name: "" });
    } catch {
      toast.error("Could not remove bank details");
    }
  };

  if (loading)
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="nb-card bg-white">Loading…</div>
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10">
      <span className="nb-label">Account</span>
      <h1 className="mt-2 font-display text-4xl font-black tracking-tighter sm:text-5xl">Settings</h1>
      <p className="mt-2 text-sm font-medium text-[#4A4A4A]">
        Manage your profile and the bank account where your sales are paid out.
      </p>

      <div className="mt-8 space-y-6">
        {/* STRIPE CONNECT */}
        <div
          data-testid="connect-card"
          className={`nb-card ${connect?.payouts_enabled ? "bg-[#B4F0A7]" : connect?.connected ? "bg-[#FFDF85]" : "bg-white"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Lightning weight="duotone" size={22} />
              <span className="nb-label">Stripe Connect Express · Payouts</span>
            </div>
            {connect?.payouts_enabled ? (
              <span data-testid="connect-active" className="nb-badge bg-white">
                <CheckCircle weight="bold" size={14} /> Connected & payouts on
              </span>
            ) : connect?.connected ? (
              <span data-testid="connect-onboarding" className="nb-badge bg-white">
                <Warning weight="bold" size={14} /> Finish onboarding
              </span>
            ) : (
              <span className="nb-badge bg-white">Not connected</span>
            )}
          </div>
          <p className="mt-3 text-sm font-medium">
            {connect?.payouts_enabled
              ? "Sales pay out directly to your Stripe balance — we never see your bank details."
              : connect?.connected
                ? "Complete the Stripe onboarding to enable payouts. Buyers can still pay you in the meantime; funds sit on the platform."
                : "Set up Stripe Connect Express to receive payouts directly. Stripe handles KYC + bank verification — we only store your Stripe account ID."}
          </p>
          {connect?.currently_due?.length > 0 && (
            <p className="mt-2 text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">
              Outstanding: {connect.currently_due.join(", ")}
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            {connect?.connected ? (
              <>
                <button
                  data-testid="connect-resume"
                  onClick={startConnect}
                  disabled={connectBusy}
                  className="nb-btn inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <ArrowSquareOut weight="bold" size={18} />
                  {connect.payouts_enabled ? "Manage in Stripe" : "Resume onboarding"}
                </button>
                {connect.dashboard_login_url && (
                  <a
                    data-testid="connect-dashboard"
                    href={connect.dashboard_login_url}
                    target="_blank"
                    rel="noreferrer"
                    className="nb-btn nb-btn-ghost inline-flex items-center gap-2"
                  >
                    <ArrowSquareOut weight="bold" size={18} /> Stripe dashboard
                  </a>
                )}
                <button
                  data-testid="connect-disconnect"
                  onClick={disconnect}
                  className="nb-btn nb-btn-danger inline-flex items-center gap-2"
                >
                  <Trash weight="bold" size={18} /> Disconnect
                </button>
              </>
            ) : (
              <button
                data-testid="connect-start"
                onClick={startConnect}
                disabled={connectBusy}
                className="nb-btn nb-btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Lightning weight="bold" size={18} /> Set up payouts with Stripe
              </button>
            )}
          </div>
        </div>

        {/* PROFILE */}
        <div className="nb-card bg-white">
          <div className="flex items-center gap-2">
            <UserIcon weight="duotone" size={22} />
            <span className="nb-label">Profile</span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="nb-label">Display name</label>
              <input
                data-testid="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="nb-input mt-2"
              />
            </div>
            <div>
              <label className="nb-label">Email</label>
              <input value={profile.email} disabled className="nb-input mt-2 opacity-70" />
            </div>
            <div>
              <label className="nb-label">Phone</label>
              <input
                data-testid="profile-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="nb-input mt-2"
                placeholder="+44 …"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="nb-label">Address</label>
              <textarea
                data-testid="profile-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                className="nb-input mt-2 resize-y"
                placeholder="Street, city, postcode"
              />
            </div>
          </div>
          <div className="mt-5">
            <button
              data-testid="save-profile"
              onClick={saveProfile}
              disabled={savingProfile}
              className="nb-btn inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FloppyDisk weight="bold" size={18} /> Save profile
            </button>
          </div>
        </div>

        {/* BANK (legacy / fallback) */}
        <div className={`nb-card ${profile?.bank ? "bg-[#B4F0A7]" : "bg-[#FFDF85]"}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Bank weight="duotone" size={22} />
              <span className="nb-label">Manual payout · Bank account (fallback)</span>
            </div>
            {profile?.bank && (
              <span data-testid="payouts-enabled" className="nb-badge bg-white">
                <CheckCircle weight="bold" size={14} /> Payouts enabled
              </span>
            )}
          </div>
          <p className="mt-3 text-sm font-medium text-[#1A1A1A]">
            {connect?.payouts_enabled
              ? "Stripe Connect is handling your payouts — you can safely remove these details."
              : profile?.bank
                ? "We'll pay out manually to this UK account within 2 working days of delivery confirmation. Consider switching to Stripe Connect above for instant payouts."
                : "Add a UK bank account so we can pay you manually until you set up Stripe Connect."}
          </p>

          {profile?.bank && (
            <div className="mt-4 rounded-xl border-[3px] border-[#0A0A0A] bg-white p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Account holder</div>
                  <div className="font-display font-bold">{profile.bank.account_holder}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Sort code</div>
                  <div className="font-display font-bold tracking-widest">{profile.bank.sort_code}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Account number</div>
                  <div className="font-display font-bold">•••• {profile.bank.account_number_last4}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Bank</div>
                  <div className="font-display font-bold">{profile.bank.bank_name || "—"}</div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="nb-label">Account holder name</label>
              <input
                data-testid="bank-account-holder"
                value={bank.account_holder}
                onChange={(e) => setBank({ ...bank, account_holder: e.target.value })}
                className="nb-input mt-2"
                placeholder="As shown on your bank statement"
              />
            </div>
            <div>
              <label className="nb-label">Sort code</label>
              <input
                data-testid="bank-sort-code"
                value={bank.sort_code}
                onChange={(e) => setBank({ ...bank, sort_code: e.target.value })}
                className="nb-input mt-2"
                placeholder="12-34-56"
                maxLength={8}
              />
            </div>
            <div>
              <label className="nb-label">Account number</label>
              <input
                data-testid="bank-account-number"
                value={bank.account_number}
                onChange={(e) => setBank({ ...bank, account_number: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                className="nb-input mt-2"
                placeholder="8 digits"
                inputMode="numeric"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="nb-label">Bank name (optional)</label>
              <input
                data-testid="bank-name"
                value={bank.bank_name}
                onChange={(e) => setBank({ ...bank, bank_name: e.target.value })}
                className="nb-input mt-2"
                placeholder="e.g. Monzo, Lloyds, Starling"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">
            <span className="inline-flex items-center gap-1"><ShieldCheck weight="bold" size={14} /> Encrypted in transit · TLS</span>
            {profile?.bank?.encrypted_at_rest && (
              <span data-testid="encrypted-at-rest" className="inline-flex items-center gap-1"><ShieldCheck weight="bold" size={14} /> Encrypted at rest · AES-128</span>
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              data-testid="save-bank"
              onClick={saveBank}
              disabled={savingBank}
              className="nb-btn inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FloppyDisk weight="bold" size={18} /> {profile?.bank ? "Update bank details" : "Save bank details"}
            </button>
            {profile?.bank && (
              <button
                data-testid="remove-bank"
                onClick={wipeBank}
                className="nb-btn nb-btn-danger inline-flex items-center gap-2"
              >
                <Trash weight="bold" size={18} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
